import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { teamApi } from '@app/api/team';
import type {
    CommentListType,
    KeyCommentList,
    TranslationCommentList,
} from '@app/features/comments/components/CommentsSidebar/types';
import { CommentList } from '@app/features/comments/components/CommentsSidebar/types';
import {
    createProjectCommentList,
    createThreadCommentList,
} from '@app/features/comments/components/CommentsSidebar/utils';
import track, { AnalyticsEventName } from '@app/utils/analytics';

type CommentState = {
    // We keep this as a separate flag to be able to toggle sidebar
    // visibility without need to reload the old data
    isSidebarOpen: boolean;
    highlightedTranslationInfo?: { keyId: string; languageId: string };
    commentListStack: CommentListType[];
};

const initialState: CommentState = {
    isSidebarOpen: false,
    commentListStack: [],
};

export const COMMENT_STACK_SESSION_KEY = '@lokalise/comment-stack';

export const saveCommentListState = (state: CommentState) => {
    sessionStorage.setItem(COMMENT_STACK_SESSION_KEY, JSON.stringify(state));
};

const hydrateCommentListState = (): CommentState | null => {
    const stored = sessionStorage.getItem(COMMENT_STACK_SESSION_KEY);

    return stored && JSON.parse(stored);
};

// We keep the state in session for now.
// Each browser tab can have its own focused comments panel.
export const loadInitialState = (): CommentState => {
    try {
        // Support for legacy chat links - they open up project comments
        const urlSearchParams = new URLSearchParams(window.location.search);

        const hasChatParam = urlSearchParams.get('chat') === '1';
        const hasCommentsParam = urlSearchParams.get('comments') === '1';

        const urlProjectId = window.location.pathname.startsWith('/project')
            ? window.location.pathname.split('/')[2]
            : undefined;

        const hydratedCommentListState = hydrateCommentListState();
        const defaultState: CommentState = {
            isSidebarOpen: hydratedCommentListState?.isSidebarOpen ?? initialState.isSidebarOpen,
            highlightedTranslationInfo:
                hydratedCommentListState?.highlightedTranslationInfo ?? initialState.highlightedTranslationInfo,
            commentListStack: hydratedCommentListState?.commentListStack ?? initialState.commentListStack,
        };

        const projectCommentListForUrlProjectId = defaultState.commentListStack.find(
            (commentList) => commentList.projectId === urlProjectId,
        );

        const urlKeyId = urlSearchParams.get('k');
        const urlCommentId = urlSearchParams.get('commentId');

        const shouldLoadThreadCommentList = urlProjectId !== undefined && urlKeyId !== null && urlCommentId !== null;

        const shouldReplaceProjectList =
            (projectCommentListForUrlProjectId === undefined && urlProjectId !== undefined) ||
            shouldLoadThreadCommentList;

        if (shouldReplaceProjectList) {
            defaultState.commentListStack = [
                createProjectCommentList({
                    projectId: urlProjectId,
                    showResolvedComments: false,
                    hasComments: undefined,
                }),
            ];

            if (shouldLoadThreadCommentList) {
                defaultState.commentListStack.unshift(
                    createThreadCommentList({
                        projectId: urlProjectId,
                        attachPointId: urlKeyId,
                        parentCommentId: urlCommentId,
                        attachPointName: '', // This will load in with the comment - Not the best solution, but it works
                        attachPointType: 'keys',
                        resolved: false,
                    }),
                );
            }
        }

        if (hasCommentsParam || hasChatParam) {
            defaultState.isSidebarOpen = true;
        }

        if (defaultState.commentListStack[0]?.type === CommentList.Thread) {
            track(AnalyticsEventName.THREAD_OPENED, {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                project_id: defaultState.commentListStack[0].projectId,
            });
        }

        return defaultState;
    } catch {
        return initialState;
    }
};

export const commentsSlice = createSlice({
    name: 'comments',
    initialState: loadInitialState(),
    reducers: {
        keyCommentsOpenedFromEditor: (state, { payload }: PayloadAction<{ keyCommentList: KeyCommentList }>) => {
            const { keyCommentList } = payload;
            state.commentListStack = [
                keyCommentList,
                createProjectCommentList({
                    projectId: keyCommentList.projectId,
                    showResolvedComments: false,
                    hasComments: undefined,
                }),
            ];
            state.isSidebarOpen = true;
        },
        translationCommentsOpenedFromEditor: (
            state,
            { payload }: PayloadAction<{ translationCommentList: TranslationCommentList }>,
        ) => {
            const { translationCommentList } = payload;
            const { keyId, languageId } = translationCommentList;
            if (keyId && languageId) {
                state.highlightedTranslationInfo = {
                    keyId,
                    languageId,
                };
            }

            state.commentListStack = [
                translationCommentList,
                createProjectCommentList({
                    projectId: translationCommentList.projectId,
                    showResolvedComments: false,
                    hasComments: undefined,
                }),
            ];
            state.isSidebarOpen = true;
        },
        newCommentStackStarted: (state, { payload }: PayloadAction<{ commentList: CommentListType }>) => {
            state.commentListStack = [payload.commentList];
            state.isSidebarOpen = true;
        },
        commentListOpened: (state, { payload }: PayloadAction<{ commentList: CommentListType }>) => {
            state.commentListStack = [payload.commentList, ...state.commentListStack];
            state.isSidebarOpen = true;
            const { commentList } = payload;
            if (commentList.type === CommentList.Translation && commentList.keyId && commentList.languageId) {
                const { keyId, languageId } = commentList;
                state.highlightedTranslationInfo = {
                    keyId,
                    languageId,
                };
            }
        },
        backToPreviousCommentList: (state) => {
            const [, ...tail] = state.commentListStack;
            state.commentListStack = tail;
            state.isSidebarOpen = true;
        },
        updateActiveCommentList: (state, { payload }: PayloadAction<{ commentList: CommentListType }>) => {
            const [, ...tail] = state.commentListStack;
            state.commentListStack = [payload.commentList, ...tail];
            state.isSidebarOpen = true;
        },
        removeActiveCommentListScrollPosition: (state) => {
            const [activeCommentList, ...tail] = state.commentListStack;
            state.commentListStack = [{ ...activeCommentList, scrollPosition: undefined }, ...tail];
            state.isSidebarOpen = true;
        },
        updateActiveCommentListScrollPosition: (
            state,
            { payload: { scrollPosition } }: PayloadAction<{ scrollPosition: number }>,
        ) => {
            const [activeCommentList, ...tail] = state.commentListStack;
            state.commentListStack = [{ ...activeCommentList, scrollPosition }, ...tail];
            state.isSidebarOpen = true;
        },
        closeSidebar: (state) => {
            state.isSidebarOpen = false;
        },
        openSidebar: (state) => {
            state.isSidebarOpen = true;
        },
    },
    extraReducers(builder) {
        builder.addMatcher(teamApi.endpoints.switchTeam.matchFulfilled, (state) => {
            // When there is a team switched, we need to close sidebar.
            // Otherwise, it would try to re-load the previously opened key/project from different team.
            state.isSidebarOpen = false;
        });
        builder.addMatcher(teamApi.endpoints.createTeam.matchFulfilled, (state) => {
            // When there is a team created, we need to close sidebar.
            // Otherwise, it would try to re-load the previously opened key/project from different team.
            state.isSidebarOpen = false;
        });
    },
});

export default commentsSlice.reducer;
